import type { EnvironmentKind, HealthState } from '@central/api';
import { Card, PageHeader } from '@central/ui';
import { Button, Group, Select, Switch } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import {
  EnvironmentsTable,
  HealthFilter,
  VersionFilter,
  type StatusChoice,
} from '../components/EnvironmentsTable';
import { useEnvironmentPages } from '../lib/environment';
import { Empty, LoadMore, QueryState } from '../components/parts';
import { useCanWrite } from '../lib/me';
import { NewEnvironmentModal } from './CustomerDialogs';

export interface EnvironmentsSearch {
  customerId?: string;
  kind?: EnvironmentKind;
  health?: HealthState;
  version?: string;
  attention?: boolean;
  archived?: boolean;
  new?: boolean;
}

/**
 * Every environment, by id, filtered by customer, kind, status and version.
 * "Needs attention" lists the environments that raise an Overview attention
 * item. Archived environments are listed only with "Show archived".
 */
export function Environments() {
  const api = useApi();
  const navigate = useNavigate({ from: '/environments' });
  const search = useSearch({ from: '/app/environments' });
  const canWrite = useCanWrite();
  const { customerId, kind, health, version, attention, archived } = search;
  const set = (patch: Partial<EnvironmentsSearch>) =>
    void navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  // The versions the fleet reports come with the overview.
  const overview = useQuery({ queryKey: keys.overview, queryFn: () => api.overview() });
  const list = useEnvironmentPages({ customerId, kind, health, version, attention, archived });
  const status: StatusChoice | undefined = attention ? 'attention' : health;
  const filtered = !!(customerId || kind || health || version || attention);

  return (
    <>
      <PageHeader
        title="Environments"
        subtitle="Every GoATMS installation, dedicated and shared"
        actions={
          canWrite && (
            <Button color="dark" size="sm" h={40} onClick={() => set({ new: true })}>
              New environment
            </Button>
          )
        }
      />
      <Group gap={10} wrap="wrap">
        <CustomerSelect
          label="Customer"
          hideLabel
          placeholder="Any customer"
          w={240}
          value={customerId ?? ''}
          onChange={(id) => set({ customerId: id || undefined })}
        />
        <Select
          aria-label="Kind"
          size="sm"
          w={160}
          maw="100%"
          value={kind ?? ''}
          onChange={(v) => set({ kind: (v || undefined) as EnvironmentKind | undefined })}
          data={[
            { value: '', label: 'Any kind' },
            { value: 'dedicated', label: 'Dedicated' },
            { value: 'shared', label: 'Shared' },
          ]}
          allowDeselect={false}
        />
        <HealthFilter
          withAttention
          value={status}
          onChange={(v) =>
            set(
              v === 'attention'
                ? { attention: true, health: undefined }
                : { attention: undefined, health: v },
            )
          }
        />
        <VersionFilter
          value={version}
          versions={overview.data?.versions ?? []}
          onChange={(v) => set({ version: v })}
        />
        <Switch
          label="Show archived"
          size="sm"
          checked={!!archived}
          onChange={(e) => set({ archived: e.currentTarget.checked || undefined })}
        />
      </Group>
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.items.length === 0 ? (
              <Empty>
                {attention
                  ? 'No environment needs attention.'
                  : filtered
                    ? 'No environments match.'
                    : 'No environments yet.'}
              </Empty>
            ) : (
              <EnvironmentsTable items={list.items} label="Environments" variant="full" />
            )
          }
        </QueryState>
        {list.hasNextPage && (
          <LoadMore
            what="environments"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          />
        )}
      </Card>
      {canWrite && search.new && (
        <NewEnvironmentModal opened onClose={() => set({ new: undefined })} />
      )}
    </>
  );
}
